import { format } from 'date-fns';
import { Award, ShieldCheck } from 'lucide-react';

const formatDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? format(d, 'MMMM d, yyyy') : '-';
};

const assessmentsText = (c) => {
  if (c.quiz_count == null) return '-';
  return c.quiz_count ? `${c.quizzes_passed} of ${c.quiz_count} passed` : 'None in this course';
};

const Detail = ({ label, value, testId, mono }) => (
  <div className="min-w-0">
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <p className={`mt-1 font-semibold text-slate-900 ${mono ? 'break-all font-mono text-xs sm:text-sm' : 'truncate font-heading text-sm sm:text-base'}`} data-testid={testId}>
      {value}
    </p>
  </div>
);

const Seal = () => (
  <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg ring-4 ring-indigo-600/15 ring-offset-4 ring-offset-[#FBFAF6] sm:h-24 sm:w-24" aria-hidden="true">
    <div className="absolute inset-1.5 rounded-full border border-dashed border-white/40" />
    <Award className="h-7 w-7 sm:h-10 sm:w-10" />
  </div>
);

// Printable certificate for a finished course builder course
export const CertificateSheet = ({ certificate }) => (
  <div id="course-certificate-sheet" className="cert-reveal relative overflow-hidden rounded-2xl bg-[#FBFAF6] p-3 text-slate-900 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.45)] sm:p-5" data-testid="certificate-sheet">
    <div className="relative rounded-xl border-2 border-indigo-500/30 p-6 sm:p-10 lg:p-14">
      <div className="pointer-events-none absolute inset-2 rounded-lg border border-indigo-500/15" />
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-indigo-500/[0.06]" />
      <div className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-emerald-500/[0.07]" />
      <div className="relative flex items-start justify-between gap-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-indigo-600" data-testid="certificate-program">{certificate.program}</p>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-5xl">Certificate of Completion</h1>
        </div>
        <Seal />
      </div>
      <div className="relative mt-10 sm:mt-14">
        <p className="text-sm text-slate-500">This certifies that</p>
        <p className="mt-2 break-words font-heading text-3xl font-semibold tracking-tight text-indigo-700 sm:text-5xl" data-testid="certificate-recipient">
          {certificate.user_name}
        </p>
        <div className="mt-4 h-px w-full max-w-md bg-gradient-to-r from-indigo-500/60 to-transparent" />
        <p className="mt-6 text-sm text-slate-600 sm:text-base">completed every lesson and passed every assessment in</p>
        <p className="mt-2 break-words font-heading text-xl font-semibold sm:text-3xl" data-testid="certificate-course-title">{certificate.course_title}</p>
      </div>
      <div className="relative mt-10 grid grid-cols-2 gap-6 border-t border-slate-200 pt-6 sm:mt-14 lg:grid-cols-4">
        <Detail label="Issued" value={formatDate(certificate.issued_at)} testId="certificate-issued-on" />
        <Detail label="Assessments" value={assessmentsText(certificate)} testId="certificate-assessments" />
        <Detail label="Average score" value={certificate.score > 0 ? `${certificate.score}%` : '-'} testId="certificate-score" />
        <Detail label="Certificate no." value={certificate.certificate_number} testId="certificate-number" mono />
      </div>
      <p className="relative mt-8 flex items-center gap-2 text-xs text-slate-500">
        <ShieldCheck className="h-4 w-4 text-emerald-600" />
        Verified digital certificate{certificate.user_email ? ` · issued to ${certificate.user_email}` : ''}
      </p>
    </div>
  </div>
);
